import {
  removeInvisibleCharacters,
  segmentString,
} from 'src/replicant/utils/string';
import TextInputView, { TextInputViewOpts } from './TextInputView';
import { PopupID } from '../popups/popupRegistry';
import { Emitter, createEmitter } from 'src/lib/Emitter';
import { animDuration, waitForItPromise } from 'src/lib/utils';
import { getMutex } from 'src/replicant/utils/mutex';

type TextInputOpts = TextInputViewOpts & {
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  value?: string;
};

export class TextInput extends TextInputView {
  public get placeholder() {
    return this._placeholder;
  }
  protected set placeholder(value: string) {
    this._placeholder = value;
    if (!this.input) return;
    this.input.placeholder = this._placeholder;
  }
  protected _placeholder: string = '';

  public get text() {
    const text = this.input?.value ?? this._text;
    return text.trim();
  }
  public set text(value: string) {
    this._text = value;
    if (!this.input) return;
    this.input.value = this._text;
  }
  protected _text: string = '';

  protected minLength: number;
  protected maxLength: number;
  protected validInput: boolean = true;
  protected attached = false;
  private emitter: Emitter<{ popupOpen: boolean }> | undefined;
  private visibilityMutex = getMutex();

  public get isValid() {
    return this.validInput;
  }

  constructor(opts: TextInputOpts) {
    super(opts);

    this.placeholder = opts.placeholder ?? '';
    this.minLength = opts.minLength ?? 0;
    this.maxLength = opts.maxLength ?? Number.POSITIVE_INFINITY;

    // Attach validator only when view is added to hierarchy.
    const onChange = this.onChange.bind(this);
    const attachListeners = () => {
      // Make sure placeholder is set.
      this.input.placeholder = this._placeholder;
      // Make sure text is set.
      this.input.value = this.text;
      this.input.addEventListener('input', onChange);
      this.attached = true;
    };
    this.on('ViewAdded', attachListeners.bind(this));
    this.on('ViewRemoved', () => {
      this.input.removeEventListener('input', onChange);
      this.attached = false;
    });

    // Check if already have a superview.
    if (opts.superview) {
      attachListeners();
    }
  }

  public updateOpts(opts: TextInputOpts) {
    const currentText = this.text;
    super.updateOpts(opts);

    this.minLength = opts.minLength ?? this.minLength;
    this.maxLength = opts.maxLength ?? this.maxLength;
    this.placeholder = opts.placeholder ?? this.placeholder;

    if (currentText !== this.text) {
      this.validate();
    }

    return opts;
  }

  private onChange() {
    // Real-time filter input for invisible characters
    const filtered = removeInvisibleCharacters(this.input.value, '');

    // Update input value only if changed, which will validate again on the next call
    if (filtered !== this.input.value) {
      this.input.value = filtered;
      return;
    }

    this.validate();
  }

  public validate() {
    // Run validator.
    this.runValidator();
    // Always emit event.
    this.emit('InputValidated', this.validInput);
  }

  protected runValidator() {
    const segmented = segmentString(this.input.value);

    // Non fixable case. Bail and set validInput to false.
    if (segmented.length < this.minLength) {
      this.validInput = false;
      return;
    }

    // Fixable case. Apply fix and set validInput to true.
    this.validInput = true;
    if (segmented.length <= this.maxLength) {
      return;
    }
    this.text = segmented.slice(0, this.maxLength).join('');
  }

  public attachToPopup(id: PopupID) {
    this.emitter?.detach();
    this.emitter = createEmitter(this, ({ ui }) => ({
      popupOpen: ui.currentPopup === id,
    }));

    this.emitter.addListener(async ({ popupOpen }) => {
      await this.visibilityMutex.lock();
      await waitForItPromise(animDuration);
      this.updateOpts({ visible: popupOpen });
      this.visibilityMutex.release();
    });
  }
}
